/**
 * Applies the story's poses to the cards' SVG parts, frame by frame.
 *
 * Direct DOM writes, not React state: the clock runs at 30 fps and React
 * has nothing to reconcile. Every write is skipped when the value has not
 * changed, so a card at rest costs nothing. Parts that are not in the DOM
 * are skipped too, so the same renderer drives a lone Enact picture (the
 * Result section's) as well as the three cards.
 */

import { OPTION_RAMPS, RAMP_ENACT, RAMP_LANTERN, RAMP_LIT, rampAt } from './wee-palette';
import {
  enactPose,
  explorePose,
  isOpen,
  tintShift,
  wishPose,
  type BarPose,
  type GlintPose,
  type HousePose,
} from './wee-poses';
import { HOUSE_H, HOUSE_W } from './house-mark';
import { LOOP_BEATS } from './wee-timeline';

const ENACT_RAMPS = [RAMP_ENACT, RAMP_LIT, RAMP_LANTERN];
const CANDIDATE_RAMPS = OPTION_RAMPS.map((own) => [RAMP_LANTERN, own]);

/** Two decimals: finer than a device pixel at any size the art is drawn. */
function n(v: number): string {
  return (Math.round(v * 100) / 100).toString();
}

function houseTransform(p: HousePose): string {
  const k = p.ink / HOUSE_W;
  return `translate(${n(p.cx - p.ink / 2)} ${n(p.cy - (HOUSE_H * k) / 2)}) scale(${(Math.round(k * 10000) / 10000).toString()})`;
}

/** A glint is a unit box: centred on its point, turned, then sized. */
function glintTransform(g: GlintPose): string {
  return `translate(${n(g.x)} ${n(g.y)}) rotate(${n((g.rotation * 180) / Math.PI)}) scale(${n(g.span)}) translate(-0.5 -0.5)`;
}

/** A pie slice from twelve o'clock, clockwise; empty until it has swept. */
function slicePath(cx: number, cy: number, r: number, start: number, sweep: number): string {
  if (sweep <= 0.0005) return '';
  const a0 = (start * 360 - 90) * (Math.PI / 180);
  const a1 = ((start + sweep) * 360 - 90) * (Math.PI / 180);
  const large = sweep > 0.5 ? 1 : 0;
  return (
    `M${n(cx)} ${n(cy)}L${n(cx + r * Math.cos(a0))} ${n(cy + r * Math.sin(a0))}` +
    `A${n(r)} ${n(r)} 0 ${large} 1 ${n(cx + r * Math.cos(a1))} ${n(cy + r * Math.sin(a1))}Z`
  );
}

/** Remembers what each element was last given, so an unchanged value is never written. */
class Writer {
  private last = new Map<Element, Map<string, string>>();

  set(el: Element | undefined, name: string, value: string): void {
    if (!el) return;
    let seen = this.last.get(el);
    if (!seen) {
      seen = new Map();
      this.last.set(el, seen);
    }
    if (seen.get(name) === value) return;
    seen.set(name, value);
    if (name.startsWith('--')) (el as HTMLElement).style.setProperty(name, value);
    else el.setAttribute(name, value);
  }
}

export type StoryRenderer = {
  render(t: number, firstPass: boolean): void;
  /** The Enact picture, for placing the page's sunrise behind its house. */
  enactArt: SVGSVGElement | null;
  /** Where the Enact house stood in the last frame, in its art's units. */
  enactHouse(): HousePose | null;
};

export function createStoryRenderer(root: HTMLElement): StoryRenderer {
  const parts = new Map<string, Element>();
  root.querySelectorAll('[data-art]').forEach((art) => {
    const beat = art.getAttribute('data-art');
    parts.set(`${beat}`, art);
    art.querySelectorAll('[data-part]').forEach((el) => {
      parts.set(`${beat}:${el.getAttribute('data-part')}`, el);
    });
  });
  const cards = new Map<string, Element>();
  root.querySelectorAll('[data-card]').forEach((el) => cards.set(el.getAttribute('data-card') ?? '', el));

  const w = new Writer();
  const part = (key: string) => parts.get(key);
  let enactHouse: HousePose | null = null;

  const stops = (gradient: Element | undefined, colours: string[]) => {
    if (!gradient) return;
    colours.forEach((c, i) => w.set(gradient.children[i], 'stop-color', c));
  };
  const circle = (el: Element | undefined, cx: number, cy: number, r: number, opacity: number) => {
    w.set(el, 'opacity', n(opacity));
    if (opacity <= 0) return;
    w.set(el, 'cx', n(cx));
    w.set(el, 'cy', n(cy));
    w.set(el, 'r', n(r));
  };
  const glint = (el: Element | undefined, g: GlintPose) => {
    w.set(el, 'opacity', n(g.opacity));
    if (g.opacity > 0) w.set(el, 'transform', glintTransform(g));
  };
  const bar = (el: Element | undefined, b: BarPose) => {
    w.set(el, 'x', n(b.x));
    w.set(el, 'y', n(b.y));
    w.set(el, 'width', n(b.w));
    w.set(el, 'height', n(b.h));
    w.set(el, 'rx', n(b.w / 2));
  };
  const drift = (beat: string, t: number) => {
    const s = tintShift(t);
    const grad = part(`${beat}:tint-grad`);
    w.set(grad, 'x1', n(-1 + s));
    w.set(grad, 'x2', n(2 + s));
  };

  function renderWish(t: number) {
    const p = wishPose(t);
    w.set(cards.get('wish'), '--a', n(p.a));
    circle(part('wish:pool'), p.house.cx, p.house.cy, p.poolR, p.lantern);
    circle(part('wish:halo'), p.house.cx, p.house.cy, p.haloR, p.halo);
    w.set(part('wish:house'), 'transform', houseTransform(p.house));
    w.set(part('wish:tint'), 'opacity', n(p.tint));
    if (p.tint > 0) drift('wish', t);
    p.glints.forEach((g, i) => glint(part(`wish:glint-${i}`), g));
  }

  function renderExplore(t: number) {
    const p = explorePose(t);
    w.set(cards.get('explore'), '--a', n(p.a));
    w.set(part('explore:rest'), 'opacity', n(p.rest.opacity));
    w.set(part('explore:rest'), 'transform', houseTransform(p.rest.house));
    if (p.tint > 0) drift('explore', t);
    p.candidates.forEach((c, i) => {
      const el = part(`explore:cand-${i}`);
      w.set(el, 'opacity', n(c.opacity));
      if (c.opacity <= 0) return;
      w.set(el, 'transform', houseTransform(c.house));
      w.set(part(`explore:cand-tint-${i}`), 'opacity', n(p.tint));
      stops(part(`explore:cand-fill-${i}`), rampAt(CANDIDATE_RAMPS[i], p.own));
    });
    p.columns.forEach((c, i) => {
      const el = part(`explore:col-${i}`);
      w.set(el, 'opacity', n(c.opacity));
      if (c.opacity <= 0) return;
      w.set(
        el,
        'transform',
        `translate(${n(c.slotX)} ${n(c.pivotY)}) scale(${n(c.scale)}) translate(${n(-c.slotX)} ${n(-c.pivotY)})`,
      );
      c.bars.forEach((b, k) => bar(part(`explore:bar-${i}-${k}`), b));
      c.pie.slices.forEach((s, k) =>
        w.set(part(`explore:slice-${i}-${k}`), 'd', slicePath(c.pie.cx, c.pie.cy, c.pie.r, s.start, s.sweep)),
      );
    });
  }

  function renderEnact(t: number, firstPass: boolean) {
    const p = enactPose(t, firstPass);
    enactHouse = p.house;
    w.set(cards.get('enact'), '--a', n(p.a));
    w.set(part('enact:house'), 'transform', houseTransform(p.house));
    stops(part('enact:fill'), rampAt(ENACT_RAMPS, p.phase));
    circle(part('enact:burst'), p.house.cx, p.house.cy, p.burst.r, p.burst.opacity);
    const glow = part('enact:glow');
    w.set(glow, 'opacity', n(p.glow));
    if (p.glow > 0) w.set(glow, 'transform', houseTransform({ ...p.house, ink: p.house.ink * 1.14 }));
    const shimmering = p.shimmer > 0 && p.shimmer < 1;
    w.set(part('enact:shimmer'), 'opacity', shimmering ? '1' : '0');
    if (shimmering) {
      // A narrow 135° band of light travelling across the mark.
      const at = -0.6 + 2.2 * p.shimmer;
      const grad = part('enact:shimmer-grad');
      w.set(grad, 'x1', n(at));
      w.set(grad, 'y1', '-0.1');
      w.set(grad, 'x2', n(at + 0.5));
      w.set(grad, 'y2', '0.6');
    }
    p.sparkles.forEach((s, i) => glint(part(`enact:spark-${i}`), s));
  }

  return {
    render(t, firstPass) {
      renderWish(t);
      renderExplore(t);
      renderEnact(t, firstPass);
      LOOP_BEATS.forEach((beat) => w.set(cards.get(beat), 'data-open', isOpen(t, beat) ? 'true' : 'false'));
    },
    enactArt: (parts.get('enact') as SVGSVGElement | undefined) ?? null,
    enactHouse: () => enactHouse,
  };
}
